import { useCallback, useEffect, useState } from 'react';
import type { HistoryEntry, LeaderboardEntry } from '../types/dashboard';
import type { GeneratedExamPaper } from '../types/generated';
import type { UserSubscription, PaymentRecord } from '../api/subscription.api';
import { getMySubscription, getPaymentHistory } from '../api/subscription.api';
import { SessionExpiredError } from '../api';
import { useAuth } from '../context/AuthContext';

const HISTORY_KEY = 'tutor_question_history';

function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as HistoryEntry[];
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]): void {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(entries));
}

export function useDashboardData() {
  const { user } = useAuth();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState<string | null>(null);

  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  useEffect(() => {
    if (!user) {
      setSubLoading(false);
      return;
    }
    setSubLoading(true);
    setSubError(null);
    Promise.all([getMySubscription(), getPaymentHistory()])
      .then(([sub, pays]) => {
        setSubscription(sub);
        setPayments(pays);
      })
      .catch((err: unknown) => {
        // The session-expired dialog already covers the dashboard here, so an inline
        // "Invalid or expired token" underneath it would just be noise.
        if (err instanceof SessionExpiredError) return;
        const e = err as { message?: string };
        setSubError(e.message ?? 'Failed to load subscription');
      })
      .finally(() => setSubLoading(false));
  }, [user]);

  const refreshSubscription = useCallback(() => {
    if (!user) return;
    Promise.all([getMySubscription(), getPaymentHistory()])
      .then(([sub, pays]) => {
        setSubscription(sub);
        setPayments(pays);
      })
      .catch(() => null);
  }, [user]);

  const addEntry = useCallback((examPaper: GeneratedExamPaper) => {
    const entry: HistoryEntry = {
      id: crypto.randomUUID(),
      generatedAt: new Date().toISOString(),
      subject: examPaper.subject,
      subjectCode: examPaper.subjectCode,
      department: examPaper.department,
      classLevel: examPaper.classLevel,
      examType: examPaper.examType,
      numberOfQuestions: examPaper.totalQuestions,
      totalMarks: examPaper.totalMarks,
      difficulty: examPaper.difficulty,
      chapters: examPaper.chapters,
      examPaper,
    };
    setHistory((prev) => {
      const next = [entry, ...prev];
      saveHistory(next);
      return next;
    });
  }, []);

  // ── Computed stats ─────────────────────────────────────────────────────────
  const daysRemaining = subscription
    ? Math.max(0, Math.ceil((new Date(subscription.periodEnd).getTime() - Date.now()) / 86400000))
    : 0;

  const thisMonthStart = new Date();
  thisMonthStart.setDate(1);
  thisMonthStart.setHours(0, 0, 0, 0);
  const questionsThisMonth = history
    .filter((e) => new Date(e.generatedAt) >= thisMonthStart)
    .reduce((sum, e) => sum + e.numberOfQuestions, 0);

  const analyticsMap = new Map<
    string,
    {
      subject: string;
      department: string;
      classLevel: number;
      count: number;
      lastGenerated: string;
    }
  >();
  for (const e of history) {
    const key = `${e.subject}__${e.department}__${e.classLevel}`;
    const existing = analyticsMap.get(key);
    if (existing) {
      existing.count += e.numberOfQuestions;
      if (e.generatedAt > existing.lastGenerated) existing.lastGenerated = e.generatedAt;
    } else {
      analyticsMap.set(key, {
        subject: e.subject,
        department: e.department,
        classLevel: e.classLevel,
        count: e.numberOfQuestions,
        lastGenerated: e.generatedAt,
      });
    }
  }
  const analyticsRows = Array.from(analyticsMap.values()).sort((a, b) => b.count - a.count);

  const myTotal = history.reduce((sum, e) => sum + e.numberOfQuestions, 0);
  const leaderboard: LeaderboardEntry[] = [
    { rank: 1, name: 'আপনি (You)', totalQuestions: myTotal, isCurrentUser: true },
  ];
  const myRank = 1;

  return {
    history,
    addEntry,
    subscription,
    payments,
    subLoading,
    subError,
    refreshSubscription,
    daysRemaining,
    questionsThisMonth,
    totalQuestions: history.reduce((s, e) => s + e.numberOfQuestions, 0),
    analyticsRows,
    leaderboard,
    myRank,
  };
}
